import React, { useEffect, useState } from 'react';
import {
    Button,
    Box,
    Typography,
    Slide,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloseIcon from '@material-ui/icons/Close';
import ShareIcon from '@material-ui/icons/Share';
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';
import IconButton from '@material-ui/core/IconButton';

const InstallPWA = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [show, setShow] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [showIosDialog, setShowIosDialog] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            const dismissed = sessionStorage.getItem('pwa_install_dismissed');
            if (!dismissed) {
                setShow(true);
            }
        };

        window.addEventListener('beforeinstallprompt', handler);

        // Detect iOS
        const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator.standalone);

        if (isIosDevice && !isInStandaloneMode) {
            setIsIOS(true);
            const dismissed = sessionStorage.getItem('pwa_install_dismissed');
            if (!dismissed) {
                setShow(true);
            }
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    const handleInstallClick = async () => {
        if (isIOS) {
            setShowIosDialog(true);
            return;
        }

        if (!deferredPrompt) {
            return;
        }

        deferredPrompt.prompt();

        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);

        setDeferredPrompt(null);
        setShow(false);
    };

    const handleDismiss = () => {
        setShow(false);
        sessionStorage.setItem('pwa_install_dismissed', 'true');
    };

    const handleCloseIosDialog = () => {
        setShowIosDialog(false);
        setShow(false);
        sessionStorage.setItem('pwa_install_dismissed', 'true');
    };

    if (!deferredPrompt && !isIOS) {
        return null;
    }

    return (
        <>
            <Slide direction="up" in={show} mountOnEnter unmountOnExit>
                <Box
                    position="fixed"
                    bottom={20}
                    left={0}
                    right={0}
                    zIndex={1300}
                    display="flex"
                    justifyContent="center"
                    px={2}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        bgcolor="background.paper"
                        boxShadow={3}
                        borderRadius={2}
                        p={2}
                        width="100%"
                        maxWidth={400}
                        border={1}
                        borderColor="divider"
                    >
                        <Box display="flex" alignItems="center">
                            <GetAppIcon color="primary" style={{ marginRight: 12 }} />
                            <Box>
                                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                    Instalar Aplicativo
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Adicione à sua tela inicial para acesso rápido
                                </Typography>
                            </Box>
                        </Box>

                        <Box display="flex" alignItems="center">
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleInstallClick}
                                style={{ textTransform: 'none', marginRight: 8 }}
                            >
                                Instalar
                            </Button>
                            <IconButton size="small" onClick={handleDismiss}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Slide>

            {/* iOS Installation Instructions Modal */}
            <Dialog
                open={showIosDialog}
                onClose={handleCloseIosDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        padding: 8
                    }
                }}
            >
                <DialogTitle style={{ textAlign: 'center', paddingBottom: 0 }}>
                    <GetAppIcon color="primary" style={{ fontSize: 48, marginBottom: 8 }} />
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Instalar Duzz Commercial
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        align="center"
                        style={{ marginBottom: 24 }}
                    >
                        Siga os passos abaixo para adicionar o app à sua tela inicial:
                    </Typography>

                    <Box display="flex" alignItems="center" mb={2} p={2} bgcolor="#f5f5f5" borderRadius={8}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                marginRight: 16,
                                flexShrink: 0
                            }}
                        >
                            <Typography variant="body2" style={{ fontWeight: 600 }}>1</Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <ShareIcon style={{ marginRight: 8, color: '#007AFF' }} />
                            <Typography variant="body2">
                                Toque no ícone <strong>Compartilhar</strong>
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" p={2} bgcolor="#f5f5f5" borderRadius={8}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                backgroundColor: theme.palette.primary.main,
                                color: 'white',
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                marginRight: 16,
                                flexShrink: 0
                            }}
                        >
                            <Typography variant="body2" style={{ fontWeight: 600 }}>2</Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <AddBoxOutlinedIcon style={{ marginRight: 8, color: '#007AFF' }} />
                            <Typography variant="body2">
                                Selecione <strong>Adicionar à Tela de Início</strong>
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center', paddingBottom: 16 }}>
                    <Button
                        onClick={handleCloseIosDialog}
                        color="primary"
                        variant="contained"
                        style={{ textTransform: 'none', borderRadius: 8, minWidth: 120 }}
                    >
                        Entendi
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default InstallPWA;
